/**
 * /admin/reports/balance-sheet — Balance Sheet.
 * @file src/web/features/admin/pages/reports/balance-sheet.tsx
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, CheckCircle2, AlertTriangle, Layers } from 'lucide-react';
import { SEO } from '../../../../shared/components';
import { AdminPageHeader } from '../../components/AdminPageHeader';
import { PageLoading } from '../../components/PageLoading';
import { adminFetchJson } from '../../lib/admin-api';
import { useCompany } from '../../lib/company-context';
import { usePeriod } from '../../lib/period-context';
import { PeriodBar } from '../../components/PeriodBar';

interface Block { name: string; amount: number; ledgers: { id: string; name: string; amount: number }[] }
interface BS { liabilities: Block[]; assets: Block[]; totals: { liabilities: number; assets: number; balanced: boolean } }

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

export default function BalanceSheetPage() {
  const { entityId } = useCompany();
  const { to } = usePeriod();
  const [data, setData] = useState<BS | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false; setData(null); setError(null);
    adminFetchJson<BS>('/api/tally-import/balance-sheet', { query: { entityId: entityId ?? undefined, to: to || undefined } })
      .then((d) => !cancel && setData(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [entityId, to]);

  const empty = data && data.liabilities.length === 0 && data.assets.length === 0;

  return (
    <>
      <SEO title="Balance Sheet" />
      <AdminPageHeader title="Balance Sheet" breadcrumbs={[{ label: 'Reports', href: '/admin/reports' }, { label: 'Balance Sheet' }]} />
      <Link to="/admin/reports" className="mb-4 inline-flex items-center gap-1 text-sm text-ink-500 no-underline hover:text-ink-700"><ChevronLeft className="h-4 w-4" /> All reports</Link>
      <PeriodBar />

      {error && <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}
      {!data && !error && <PageLoading label="Building balance sheet" />}
      {empty && <EmptyState />}

      {data && !empty && (
        <>
          <div className="mb-4">
            {data.totals.balanced
              ? <span className="inline-flex items-center gap-1.5 rounded-full bg-ok-50 px-3 py-1 text-xs font-medium text-ok-600"><CheckCircle2 className="h-3.5 w-3.5" /> Balanced</span>
              : <span className="inline-flex items-center gap-1.5 rounded-full bg-warn-50 px-3 py-1 text-xs font-medium text-warn-600"><AlertTriangle className="h-3.5 w-3.5" /> Out of balance</span>}
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Side title="Liabilities" blocks={data.liabilities} total={data.totals.liabilities} />
            <Side title="Assets" blocks={data.assets} total={data.totals.assets} />
          </div>
        </>
      )}
    </>
  );
}

function Side({ title, blocks, total }: { title: string; blocks: Block[]; total: number }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-paper">
      <div className="border-b border-ink-100 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-ink-400">{title}</div>
      <table className="w-full text-sm">
        <tbody>
          {blocks.map((b) => (
            <tr key={b.name} className="border-t border-ink-50">
              <td className="px-5 py-2.5 font-medium text-ink-800">{b.name}</td>
              <td className="px-5 py-2.5 text-right tabular-nums text-ink-800">{inr(b.amount)}</td>
            </tr>
          ))}
          <tr className="border-t-2 border-ink-200 bg-ink-50 font-semibold text-ink-900">
            <td className="px-5 py-3">Total</td>
            <td className="px-5 py-3 text-right tabular-nums">{inr(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-ink-100 text-ink-400"><Layers className="h-6 w-6" /></span>
      <h3 className="mt-5 text-lg font-semibold text-ink-900">Nothing to show</h3>
      <p className="mt-1.5 text-sm text-ink-600">Import Tally data for this entity to see its balance sheet.</p>
    </div>
  );
}
